import {
  STORAGE_DEPARTMENT_LABEL,
  STORAGE_TYPE_LABEL,
  type StorageDepartment,
} from "@nexseed/shared/masters";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  ChevronRight,
  Copy,
  FileUp,
  Pencil,
  Plus,
  Warehouse,
} from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { SearchField } from "@/components/app/search-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { isStoresOnly } from "@nexseed/shared";
import { useAuth } from "@/features/auth/store";
import { CopyTreeDialog } from "@/features/masters/locations/copy-dialog";
import { ImportBinMasterDialog } from "@/features/masters/locations/import-dialog";
import {
  LocationNodeSheet,
  type LocationRow,
} from "@/features/masters/locations/node-sheet";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/masters/locations")({
  // Both special screens carry their own codes now, so the URL is guarded too — the side rail
  // hiding an entry has never stopped anyone typing the path.
  beforeLoad: () => {
    if (!useAuth.getState().can("locations.view")) throw redirect({ to: "/" });
  },
  component: Locations,
});

const KIND_LABEL: Record<LocationRow["kind"], string> = {
  warehouse: "Warehouse",
  location: "Location",
  bin: "Bin",
  sub_bin: "Sub-bin",
};
const CHILD_LABEL: Record<LocationRow["kind"], string | null> = {
  warehouse: "location",
  location: "bin",
  bin: "sub-bin",
  sub_bin: null,
};

type Sheet =
  | { mode: "create"; parent: LocationRow | null }
  | { mode: "edit"; row: LocationRow }
  | { mode: "copy"; row: LocationRow };

/**
 * Storage tree explorer (Warehouse → Location → Bin → Sub-bin) with node editing and the bin-master
 * import. Paths start at the location ("WH-01/A01/3"); the warehouse is the node above it.
 */
function Locations() {
  const { facilityId, can } = useAuth();
  const manage = can("locations.manage");
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Which racks to show: every one, or one department's (with the WHs above them). A Stores-only
  // login sees the store's racks alone (user, 2026-10-17: "Warehouses only include stores bins").
  const storesOnly = isStoresOnly(can);
  const [pickedDept, setDept] = useState<"all" | StorageDepartment>("all");
  const dept = storesOnly ? "stores" : pickedDept;
  const tree = useQuery({
    queryKey: ["locations", "tree", facilityId, "admin", dept],
    queryFn: () =>
      api<LocationRow[]>(
        `/locations/tree?facilityId=${facilityId}&includeInactive=true&department=${dept}`
      ),
    enabled: facilityId !== null,
    staleTime: 60_000,
  });
  const counts = useQuery({
    queryKey: ["locations", "counts", facilityId, storesOnly],
    queryFn: () =>
      api<{
        warehouse: number;
        location: number;
        bin: number;
        sub_bin: number;
      }>(`/locations/counts?facilityId=${facilityId}`),
    enabled: facilityId !== null,
  });
  const c = counts.data;
  const byId = useMemo(
    () => new Map((tree.data ?? []).map((n) => [n.id, n])),
    [tree.data]
  );
  /** What a node follows when it sets nothing: its parent's department. */
  const parentOf = (n: { parentId: number | null } | null) =>
    n?.parentId ? byId.get(n.parentId) : undefined;
  /** The warehouse a node sits under, to know a Store-type tree (user, 2026-10-18). */
  const rootOf = (n: LocationRow | null | undefined) => {
    let r = n;
    while (r?.parentId) r = byId.get(r.parentId);
    return r;
  };
  const children = useMemo(() => {
    const m = new Map<number | null, LocationRow[]>();
    for (const n of tree.data ?? [])
      m.set(n.parentId, [...(m.get(n.parentId) ?? []), n]);
    return m;
  }, [tree.data]);
  const matches = useMemo(() => {
    const t = q.trim().toUpperCase();
    return t
      ? (tree.data ?? [])
          .filter((n) => n.path.toUpperCase().includes(t))
          .slice(0, 200)
      : [];
  }, [q, tree.data]);

  if (facilityId === null)
    return <p className="text-ink-muted">Select a plant first.</p>;
  return (
    <>
      <PageHeader
        title="Warehouses"
        description={
          storesOnly
            ? "The store's racks at this plant: Warehouse → Location → Bin → Sub-bin. Capacity 0 means unlimited."
            : "Storage hierarchy at this plant: Warehouse → Location → Bin → Sub-bin. Codes are canonical (zero-padded); capacity 0 means unlimited."
        }
        meta={
          c && (
            <>
              {(["warehouse", "location", "bin", "sub_bin"] as const).map(
                (k) => (
                  <Badge key={k} tone="neutral">
                    {KIND_LABEL[k]}s{" "}
                    <span className="num ml-1 font-semibold text-ink">
                      {c[k]}
                    </span>
                  </Badge>
                )
              )}
            </>
          )
        }
        actions={
          manage ? (
            <>
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                <FileUp className="h-4 w-4" /> Import bin master
              </Button>
              <Button
                onClick={() => setSheet({ mode: "create", parent: null })}
              >
                <Plus className="h-4 w-4" /> New warehouse
              </Button>
            </>
          ) : undefined
        }
      />
      <Card>
        <CardHeader>
          <CardTitle>Tree</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {!storesOnly && (
              <Select
                aria-label="Used by"
                className="w-44"
                value={dept}
                onChange={(e) =>
                  setDept(e.target.value as "all" | StorageDepartment)
                }
              >
                <option value="all">Seed and Stores</option>
                <option value="seed">Seed racks</option>
                <option value="stores">Stores racks</option>
              </Select>
            )}
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Find a bin by code…"
              ariaLabel="Find a bin"
              hotkey={null}
            />
          </div>
        </CardHeader>
        <CardBody className="p-2">
          {tree.isLoading && <Skeleton lines={5} className="p-3" />}
          {q.trim() ? (
            <ul className="divide-y divide-border text-sm">
              {matches.length === 0 && (
                <li className="p-3 text-ink-muted">No bin matches.</li>
              )}
              {matches.map((n) => (
                <li
                  key={n.id}
                  className="flex items-center gap-2 px-3 py-1.5 font-mono"
                >
                  {n.path}
                  <Badge tone="neutral">{KIND_LABEL[n.kind]}</Badge>
                  <DeptBadge node={n} flat />
                  {n.isQuarantine && <Badge tone="warning">quarantine</Badge>}
                  {!n.isActive && <Badge tone="danger">inactive</Badge>}
                  {manage && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="ml-auto"
                      aria-label={`Edit ${n.path}`}
                      onClick={() => setSheet({ mode: "edit", row: n })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <TreeLevel
              nodes={children.get(null) ?? []}
              childrenOf={children}
              depth={0}
              manage={manage}
              onAction={setSheet}
            />
          )}
          {!tree.isLoading &&
            (children.get(null) ?? []).length === 0 &&
            !q.trim() && (
              <p className="p-3 text-sm text-ink-muted">
                {dept === "all"
                  ? "No warehouses at this plant yet — create one or import the bin master."
                  : storesOnly
                    ? "No Store-type warehouse at this plant yet — create one with New warehouse."
                    : `No ${STORAGE_DEPARTMENT_LABEL[dept]} racks at this plant yet — open a WH or bin under “Seed and Stores” and set its Used by.`}
              </p>
            )}
        </CardBody>
      </Card>
      {sheet?.mode === "copy" && (
        <CopyTreeDialog source={sheet.row} onClose={() => setSheet(null)} />
      )}
      {sheet && sheet.mode !== "copy" && (
        <LocationNodeSheet
          facilityId={facilityId}
          parent={sheet.mode === "create" ? sheet.parent : null}
          row={sheet.mode === "edit" ? sheet.row : undefined}
          inStoreTree={
            rootOf(sheet.mode === "create" ? sheet.parent : parentOf(sheet.row))
              ?.storageType === "store"
          }
          storesOnly={storesOnly}
          inherited={
            (sheet.mode === "create" ? sheet.parent : parentOf(sheet.row))
              ?.effectiveDepartment ?? "seed"
          }
          parentPath={
            (sheet.mode === "create" ? sheet.parent : parentOf(sheet.row))?.path
          }
          onClose={() => setSheet(null)}
        />
      )}
      <ImportBinMasterDialog
        facilityId={facilityId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </>
  );
}

function TreeLevel({
  nodes,
  childrenOf,
  depth,
  manage,
  onAction,
}: {
  nodes: LocationRow[];
  childrenOf: Map<number | null, LocationRow[]>;
  depth: number;
  manage: boolean;
  onAction: (s: Sheet) => void;
}) {
  return (
    <ul className={cn(depth > 0 && "border-l border-border pl-3")}>
      {nodes.map((n) => (
        <TreeNode
          key={n.id}
          node={n}
          childrenOf={childrenOf}
          depth={depth}
          manage={manage}
          onAction={onAction}
        />
      ))}
    </ul>
  );
}

function TreeNode({
  node,
  childrenOf,
  depth,
  manage,
  onAction,
}: {
  node: LocationRow;
  childrenOf: Map<number | null, LocationRow[]>;
  depth: number;
  manage: boolean;
  onAction: (s: Sheet) => void;
}) {
  const kids = childrenOf.get(node.id) ?? [];
  const [open, setOpen] = useState(depth === 0);
  const childLabel = CHILD_LABEL[node.kind];
  return (
    <li>
      <div className="group flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-surface-sunken">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={kids.length ? open : undefined}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {kids.length ? (
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-ink-muted transition-transform",
                open && "rotate-90"
              )}
            />
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          {node.kind === "warehouse" && (
            <Warehouse className="h-4 w-4 shrink-0 text-primary-text" />
          )}
          <span className="font-mono">{node.code}</span>
          {node.name && (
            <span className="truncate text-ink-muted">{node.name}</span>
          )}
          <Badge tone="neutral">{KIND_LABEL[node.kind]}</Badge>
          {kids.length > 0 && (
            <span className="num text-xs text-ink-muted">{kids.length}</span>
          )}
          {node.storageType === "store" && (
            <Badge tone="info">{STORAGE_TYPE_LABEL.store} type</Badge>
          )}
          <DeptBadge node={node} />
          {node.isQuarantine && <Badge tone="warning">quarantine</Badge>}
          {!node.isActive && <Badge tone="danger">inactive</Badge>}
          {Number(node.capacityKg) > 0 && (
            <span className="num ml-auto text-xs text-ink-muted">
              {Number(node.capacityKg).toLocaleString("en-IN")} kg
            </span>
          )}
        </button>
        {manage && (
          <span className="flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
            {childLabel && (
              <Tooltip content={`Add ${childLabel}`}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Add ${childLabel} under ${node.path}`}
                  onClick={() => onAction({ mode: "create", parent: node })}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </Tooltip>
            )}
            {node.kind === "warehouse" && (
              <Tooltip content="Copy as…">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Copy ${node.code}`}
                  onClick={() => onAction({ mode: "copy", row: node })}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </Tooltip>
            )}
            <Tooltip content="Edit">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Edit ${node.path}`}
                onClick={() => onAction({ mode: "edit", row: node })}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </Tooltip>
          </span>
        )}
      </div>
      {open && kids.length > 0 && (
        <TreeLevel
          nodes={kids}
          childrenOf={childrenOf}
          depth={depth + 1}
          manage={manage}
          onAction={onAction}
        />
      )}
    </li>
  );
}

/**
 * Who the rack is allotted to, where that is set on it (a Stores WH, a Stores bin inside a Seed WH,
 * a Seed bin carved out of a Stores WH); its children follow it unmarked, since the tree shows it
 * above them. `flat` (the search results, with no parent in view) marks every Stores rack.
 */
function DeptBadge({ node, flat }: { node: LocationRow; flat?: boolean }) {
  if (node.department)
    return (
      <Badge tone={node.department === "stores" ? "info" : "neutral"}>
        {STORAGE_DEPARTMENT_LABEL[node.department]}
      </Badge>
    );
  if (flat && node.effectiveDepartment === "stores")
    return <Badge tone="info">Stores</Badge>;
  return null;
}
